import React, { useEffect } from 'react';

import { App } from '@flipdish/api-client-typescript';
import { connect } from 'react-redux';
import { checkVisibility } from 'react-redux-permissions/dist/core';
import { Redirect, Route, RouteComponentProps, Switch } from 'react-router';
import { compose } from 'recompose';

import LazyComponent from '@fd/ui/LazyComponent';

import { lazyWithRetry } from '../../helpers/utilities';
import ErrorBoundary from '../../layouts/Portal/ErrorBoundary';
import { isFlipdishGlobal } from '../../selectors/app.selector';
import { flagService } from '../../services/flagService/flagService';
import FinanceMicroFrontendRoutes from '../FinanceMicrofrontend/Finance.routes';
import {
  reset,
  subscribeBankingUpdates,
  unSubscribeBankingUpdates,
} from './Banking/banking.actions';
import BankingCreate from './Banking/Create/BankingCreate';
import BankingDetails from './Banking/Details/BankingDetails';
import BankingDetailsV2 from './Banking/Details/BankingDetailsV2';
import BankingEdit from './Banking/Edit/BankingEdit';
import BankingList from './Banking/List/BankingList';
import ClientReportingPayoutsMFE from './ClientReportingPayoutsMFE';
import PayoutDetails from './Payouts/components/PayoutDetails/PayoutDetails';
import PayoutReports from './Payouts/components/PayoutReports';
import RestaurantVoucherCreate from './RestaurantVouchers/Create/RestaurantVoucherCreate';
import RestaurantVouchersList from './RestaurantVouchers/List/RestaurantVouchersList';

const PayoutReportV3 = lazyWithRetry(
  () => import('./Payouts/components/PayoutDetailsV3/PayoutReportV3')
);
type Props = MappedProps & MappedDispatch & RouteComponentProps;

const FinanceRoutes: React.FC<React.PropsWithChildren<Props>> = (props) => {
  const {
    canViewBankAccounts,
    canViewPayouts,
    enableClientReportingPayoutsMfe,
    isFlipdishGlobal,
    match: { path, url },
    canCreate,
    reset,
    unSubscribeBankingUpdates,
    subscribeBankingUpdates,
    enableTailwindBankDetails,
  } = props;

  useEffect(() => {
    subscribeBankingUpdates();
    return () => {
      unSubscribeBankingUpdates();
      reset();
    };
  }, []);

  return (
    <ErrorBoundary identifier="bank-accounts">
      <Switch>
        {canViewBankAccounts && canCreate && !isFlipdishGlobal && (
          <Route exact path={`${path}/bank-accounts/new`} component={BankingCreate} />
        )}

        {canViewBankAccounts && enableTailwindBankDetails && (
          <Route exact path={`${path}/bank-accounts/:accountId`} component={BankingDetailsV2} />
        )}
        {canViewBankAccounts && !enableTailwindBankDetails && (
          <Route exact path={`${path}/bank-accounts/:accountId`} component={BankingDetails} />
        )}

        {canViewBankAccounts && canCreate && (
          <Route exact path={`${path}/bank-accounts/:accountId/edit`} component={BankingEdit} />
        )}

        {canViewBankAccounts && canCreate && !isFlipdishGlobal && (
          <Route
            exact
            path={`${path}/restaurant-vouchers/new`}
            component={RestaurantVoucherCreate}
          />
        )}

        {canViewPayouts && enableClientReportingPayoutsMfe && (
          <Route exact path={`${path}/payouts`} component={ClientReportingPayoutsMFE} />
        )}
        {canViewPayouts && !enableClientReportingPayoutsMfe && (
          <Route exact path={`${path}/payouts`} component={PayoutReports} />
        )}
        {canViewPayouts && enableClientReportingPayoutsMfe && (
          <Route exact path={`${path}/payouts/:payoutId`} component={ClientReportingPayoutsMFE} />
        )}
        {canViewPayouts && !enableClientReportingPayoutsMfe && (
          <Route exact path={`${path}/payouts/:payoutId`} component={PayoutDetails} />
        )}
        {canViewPayouts && enableClientReportingPayoutsMfe && (
          <Route
            exact
            path={`${path}/payoutsv3/:payoutId`}
            render={(routeProps) => {
              const { payoutId } = routeProps.match.params as { payoutId: string };
              return (
                <Redirect
                  to={{
                    pathname: `${url}/payouts/${payoutId}`,
                    search: routeProps.location.search,
                  }}
                />
              );
            }}
          />
        )}
        {canViewPayouts && !enableClientReportingPayoutsMfe && (
          <Route
            exact
            path={`${path}/payoutsv3/:payoutId`}
            render={() => (
              <LazyComponent>
                <PayoutReportV3 />
              </LazyComponent>
            )}
          />
        )}
        {canViewBankAccounts && <Route path={`${path}/bank-accounts`} component={BankingList} />}
        {canViewBankAccounts && (
          <Redirect exact from={`${path}/banking`} to={`${path}/bank-accounts`} />
        )}
        {canViewBankAccounts && (
          <Redirect exact from={`${path}/banking/`} to={`${path}/bank-accounts`} />
        )}
        {canViewPayouts && (
          <Redirect
            exact
            from={`${path}/bankaccounts/:bankAccountId/payouts/:payoutId`}
            to={`${path}/payouts/:payoutId/?bankAccountId=:bankAccountId`}
          />
        )}
        {canViewPayouts && !canViewBankAccounts && (
          <Redirect from={`${path}/bank-accounts`} to={`${path}/payouts`} />
        )}
        {canViewPayouts && !canViewBankAccounts && (
          <Redirect from={`${path}/banking`} to={`${path}/payouts`} />
        )}
        {canViewBankAccounts && !canViewPayouts && (
          <Redirect from={`${path}/payouts`} to={`${path}/bank-accounts`} />
        )}
        {canViewBankAccounts && !canViewPayouts && (
          <Redirect from={`${path}/payoutsv3`} to={`${path}/bank-accounts`} />
        )}
        {canViewBankAccounts && !canViewPayouts && (
          <Redirect
            from={`${path}/bankaccounts/:bankAccountId/payouts/:payoutId`}
            to={`${path}/bank-accounts`}
          />
        )}
        <Redirect
          exact
          from={`${path}`}
          to={`${path}/${canViewPayouts ? 'payouts' : 'bank-accounts'}`}
        />
        {canViewBankAccounts && (
          <Route path={`${path}/restaurant-vouchers`} component={RestaurantVouchersList} />
        )}
        {canViewBankAccounts && <Route path={`${path}`} component={FinanceMicroFrontendRoutes} />}
        {canViewPayouts && <Redirect to={`${path}/payouts`} />}
      </Switch>
    </ErrorBoundary>
  );
};

type MappedProps = ReturnType<typeof mapStateToProps>;
function mapStateToProps(state: AppState) {
  const canCreate = checkVisibility(
    state.permissions,
    [App.AppResourceSetEnum.CreateBankAccounts],
    []
  );
  const canViewBankAccounts = checkVisibility(
    state.permissions,
    [App.AppResourceSetEnum.ViewBankAccounts, 'FlipdishStaff'],
    []
  );
  const canViewPayouts = checkVisibility(
    state.permissions,
    [App.AppResourceSetEnum.ViewPayouts, 'FlipdishStaff'],
    []
  );
  return {
    canCreate,
    canViewBankAccounts,
    canViewPayouts,
    enableClientReportingPayoutsMfe: flagService.isFlagOn(state, 'enableClientReportingPayoutsMfe'),
    enableTailwindBankDetails: flagService.isFlagOn(state, 'enableTailwindBankDetails'),
    isFlipdishGlobal: isFlipdishGlobal(state),
  };
}
type MappedDispatch = ReturnType<typeof mapDispatchToProps>;
const mapDispatchToProps = (dispatch: ThunkDispatch) => ({
  subscribeBankingUpdates: () => dispatch(subscribeBankingUpdates()),
  unSubscribeBankingUpdates: () => dispatch(unSubscribeBankingUpdates()),
  reset: () => {
    dispatch(reset());
  },
});

export { FinanceRoutes };
export default compose<Props, RouteComponentProps>(connect(mapStateToProps, mapDispatchToProps))(
  FinanceRoutes
);
